// The admin Courses subcategory tree of each category, shared by the admin Courses pages
// (pages/admin/coursesShared.jsx) and the coach Syllabus Tracker's Category › Subcategory pickers.
import { SIDEBAR_TREE } from '../data/learningZoneMockData';
import {
  GROUP_CATEGORIES,
  addCustomSubcategoryRows,
  buildLearningZoneTree,
} from '../components/student/learningzone/courseSubcategoryRows';
import { withoutHiddenRows } from '../components/student/learningzone/hiddenLearningZoneRows';

const lower = (value) => String(value || '').trim().toLowerCase();

// The course categories the student Learning Zone sidebar shows, in its order, with the name and icon
// students see there ({ name, label, icon }; category "Art" is "Fun with Art").
export const LEARNING_ZONE_CATEGORIES = SIDEBAR_TREE.filter((row) => GROUP_CATEGORIES[row.id]).map((row) => ({
  name: GROUP_CATEGORIES[row.id],
  label: row.label,
  icon: row.icon,
}));

// What admins see for a category: the student Learning Zone's name ("Art" -> "Fun with Art"), or its own.
export const categoryLabel = (name) =>
  LEARNING_ZONE_CATEGORIES.find((category) => lower(category.name) === lower(name))?.label || name;

/**
 * Admin categories (GET .../courses/categories) as the student Learning Zone shows them: its
 * categories first, in its order, with its names and icons (as `label` / `icon`; `name` stays the
 * course's category), then the others in their own order.
 */
export const withLearningZoneLook = (categories = []) => {
  const rank = (category) => {
    const i = LEARNING_ZONE_CATEGORIES.findIndex((lz) => lower(lz.name) === lower(category.name));
    return i === -1 ? LEARNING_ZONE_CATEGORIES.length : i;
  };
  return categories
    .map((category, i) => ({ category, i, rank: rank(category) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map(({ category, rank: r }) => {
      const look = LEARNING_ZONE_CATEGORIES[r];
      return look ? { ...category, label: look.label, icon: look.icon } : { ...category, label: category.name };
    });
};

/**
 * `rows` = { subcategories, taskTypes, hiddenRows } from GET .../courses/subcategories (built-in rows
 * admins deleted, `hiddenRows`, are left out with the rows under them). Returns
 * - nodesFor(category): the category's subcategories, the same rows the student Learning Zone shows
 *   under it: its built-in rows (English > Action Songs > Vocal Songs, Scenes ...), the task type rows
 *   of its published courses (English > Poem) and the ones admins added. Each node is
 *   { key, label, icon, kind: "builtIn" | "taskType" | "added", taskType, syllabusTopic, children };
 *   `syllabusTopic` is the coach Syllabus Tracker topic the row belongs to (from its own row or the
 *   nearest row above it, e.g. Action Songs > Vocal Songs > Hindi -> "Action Songs"), or "".
 * - topicFor(category): the Syllabus Tracker topic of the whole category (Computer Apps ->
 *   "Computer apps"), or "".
 * - categoryNames: the categories with subcategories, Learning Zone groups first in sidebar order,
 *   then the others admins added subcategories to.
 */
export function buildSubcategoryNodes({ subcategories = [], taskTypes = [], hiddenRows = [] } = {}) {
  const built = buildLearningZoneTree(SIDEBAR_TREE, taskTypes, subcategories);
  const { views } = built;
  const tree = withoutHiddenRows(built.tree, { hiddenRows });
  const toNode = (row, topicAbove = '') => {
    const taskType = !row.subcategoryId && views[row.view] ? views[row.view].taskType : '';
    const syllabusTopic = row.syllabusTopic || topicAbove;
    return {
      key: row.subcategoryId || row.id,
      label: row.label,
      icon: row.icon,
      kind: row.subcategoryId ? 'added' : taskType ? 'taskType' : 'builtIn',
      taskType,
      syllabusTopic,
      children: (row.children || []).map((child) => toNode(child, syllabusTopic)),
    };
  };
  const groupOf = (categoryName) =>
    tree.find((g) => GROUP_CATEGORIES[g.id] && lower(GROUP_CATEGORIES[g.id]) === lower(categoryName));

  const nodesFor = (categoryName) => {
    if (!categoryName) return [];
    const group = groupOf(categoryName);
    if (group) return (group.children || []).map((row) => toNode(row, group.syllabusTopic || ''));
    // Not in the Learning Zone: only the subcategories added to it.
    const own = addCustomSubcategoryRows([{ id: 'category', children: [] }], subcategories, { category: categoryName });
    return (own.tree[0].children || []).map((row) => toNode(row));
  };
  const topicFor = (categoryName) => (categoryName ? groupOf(categoryName)?.syllabusTopic || '' : '');

  const names = [];
  const seen = new Set();
  const addName = (name) => {
    if (!name || seen.has(lower(name))) return;
    seen.add(lower(name));
    names.push(name);
  };
  tree.forEach((group) => GROUP_CATEGORIES[group.id] && addName(GROUP_CATEGORIES[group.id]));
  subcategories.forEach((sub) => addName(sub.category));
  const categoryNames = names.filter((name) => topicFor(name) || nodesFor(name).length > 0);

  return { nodesFor, topicFor, categoryNames };
}
